/**
 * Component delivery-slot-calendar
 *
 * Shows a calender for the user to specify a delivery slot
 */
const dayjs = require('dayjs');
const DomPositionTracker = require('../../../utils/domPositionTracker.es6').default;
const Component = require('../../component');

const template = '<div data-component="delivery-day-calendar"></div>';

/**
 *
 * @param deliveryDays this is the delivery days that show the delivery slots
 * @param selectedDate [dayjs] the date that is currently
 * selected (the state for this is held in the delivery-slot-picker)
 * @param {HTMLElement} targetElement element calender wil be positioned relative too
 * @param currentDate [dayjs] the date to decide where the slots are shown on
 * calendar (useful for testing)
 * @constructor
 */
export default class DeliveryDayCalendar {

  constructor(deliveryDays, selectedDate, targetElement, currentDate) {
    const now = currentDate ? currentDate : dayjs();// used in subsequent comparisons
    let comparisonDate;
    this.element = Component.create(template);
    this.domPositionTracker = new DomPositionTracker(this.element, targetElement,
      offsetsAndDimensions);
    // we need to store this because we need this 'context'
    this.destroyBoundWithThis = this.destroy.bind(this);

    this.state = {};

    // filter, map and sort the slots into their respective days, earlier dates first (Acc)
    this.state.firstMonthDeliveryDatesAsc = deliveryDays.filter((deliveryDay) => {
      comparisonDate = dayjs(
        deliveryDay.deliverySlots[deliveryDay.deliverySlots.length - 1].date.replace(/\//g, '-') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
      );
      return comparisonDate.month() === now.month();
    }).map((deliveryDay) => { // eslint-disable-line arrow-body-style
      return dayjs(
        deliveryDay.deliverySlots[deliveryDay.deliverySlots.length - 1].date.replace(/\//g, '-') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
      );
    }).sort((item1, item2) => { // eslint-disable-line arrow-body-style
      return item1.isAfter(item2) ? 1 : -1;
    });

    this.state.secondMonthDeliveryDatesAsc = deliveryDays.filter((deliveryDay) => {
      comparisonDate = dayjs(
        deliveryDay.deliverySlots[deliveryDay.deliverySlots.length - 1].date.replace(/\//g, '-') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
      );
      return comparisonDate.month() === now.clone().add(1, 'months').month();
    }).map((deliveryDay) => { // eslint-disable-line arrow-body-style
      return dayjs(
        deliveryDay.deliverySlots[deliveryDay.deliverySlots.length - 1].date.replace(/\//g, '-') // replace converts 'YYYY/MM/DD' to 'YYYY-MM-DD'
      );
    }).sort((item1, item2) => { // eslint-disable-line arrow-body-style
      return item1.isAfter(item2) ? 1 : -1;
    });

    // default to first date
    // this.state.selectedDate = this.state.firstMonthDeliveryDatesAsc[0];

    this.generateContent();
    this.addListeners();
    this.applySelectedDate(selectedDate);
  }

  /**
   * updates the cal UI
   * @param {dayjs} selectedDate
   */
  applySelectedDate(selectedDate) {
    const q = `[data-date="${selectedDate.format('YYYY/MM/DD')}"]`;
    const elementToBeChanged = this.element.querySelector(q);
    elementToBeChanged.className += ' selected';
  }

  /**
   * generate the calendar table
   *
   * Rules:
   * 1: Each month must not show dates in a different month
   */
  generateContent() {
    const nextMonthHasSlots = this.state.secondMonthDeliveryDatesAsc.length > 0;

    const month1HTML = this.monthTable(this.state.firstMonthDeliveryDatesAsc,
      nextMonthHasSlots) || '';
    const month2HTML = this.monthTable(this.state.secondMonthDeliveryDatesAsc, false) || '';

    this.element.innerHTML = `<a class="closeBtn">Close&nbsp;x</a>
                                ${month1HTML}
                                ${month2HTML}
                                <ul class="availabilityLegend">
                                  <li><span class="unavailable"></span>Unavailable date</li>
                                  <li><span class="deliverable"></span>Available to pick</li>
                                  <li><span class="selected"></span>Chosen/selected date</li>
                                </ul>
                                `;
  }

  /**
   *  any click on the calendar or the body will close the calendar, we just inspect the target to
   *  determine what action to take
   */
  addListeners() {
    // when this is clicked just close the calendar as it has 0 effect on the delivery slots
    //
    // deterin
    const viewportWidth = window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;

    this.eventElement = viewportWidth > 1024 ? document : this.element;
    
    this.eventElement.addEventListener('mouseup', this.destroyBoundWithThis);
  }

  /**
   * Renders the map
   */
  show() {
    const el = document.createElement('DIV');
    el.className = 'modal-background';
    document.body.appendChild(el);
    document.body.appendChild(this.element);
  }

  /** @returns {void} */
  destroy() {
    document.body.removeChild(document.querySelector('.modal-background'));
    this.eventElement.removeEventListener('mouseup', this.destroyBoundWithThis);

    this.element.parentElement.removeChild(this.element);
    this.domPositionTracker.destroy();
  }

  // todo possible move to util lib
  /**
   * Generates a a header and table for a month of dates
   * @param {Array<dayjs>} dates the currents months dates to render
   * @param {boolean} hasFollowingMonth is there slots in next month

   * @returns {*}
   */
  monthTable(dates, hasFollowingMonth) {
    let monthHTML = '';
    let weekIndex = 0; // used to jump between week rows

    // this might be the previous month, or next
    // (we don't show these but they can take up blank space)
    let firstMonthStartDate;

    const firstDeliveryDate = dates[0];

    if (!dates.length) {
      return false;
    }

    if (firstDeliveryDate.day() === 0) { // if its sunday get previous weeks monday
      firstMonthStartDate = firstDeliveryDate.clone().subtract(6, 'days');
    } else { // set it to monday
      firstMonthStartDate = firstDeliveryDate.clone().set('day', 1);
    }

    // this will be the next sunday, or same sunday of the last delivery date of this month
    // or the last sunday of the month if hasFollowingMonth
    let firstMonthEndDate;
    if (hasFollowingMonth) {
      firstMonthEndDate = firstDeliveryDate.clone().endOf('month');
    } else {
      firstMonthEndDate = dates[dates.length - 1].clone();
    }

    if (firstMonthEndDate.day() !== 0) { // add the days to make up the end of the render week
      firstMonthEndDate = firstMonthEndDate.add(7 - firstMonthEndDate.day(), 'day');
    }

    // we bump this up a day each time we render a td
    let incrementingDate = firstMonthStartDate.clone();
    // render date if in same month
    while (isFirstDateSameOrBeforeSecondDate(incrementingDate, firstMonthEndDate)) {

      // begin the row if its the start
      monthHTML += (weekIndex % 7 === 0 ? '<tr>' : '');

      if (incrementingDate.month() === firstDeliveryDate.month()) {

        // eslint-disable-next-line arrow-body-style
        const isDateDeliverable = dates.some((deliveryDate) => { // eslint-disable-line no-loop-func
          return incrementingDate.date() === deliveryDate.date();
        });

        const dateClass = isDateDeliverable ? 'deliverable' : '';
        monthHTML += `
          <td class="${dateClass}" data-date="${incrementingDate.format('YYYY/MM/DD')}">
            ${incrementingDate.date()}
          </td>
        `;

      } else {
        // render empty cell
        monthHTML += '<td data-empty></td>';
      }

      // close the row if its the end
      monthHTML += (weekIndex % 7 === 6 ? '</tr>' : '');
      incrementingDate = incrementingDate.add(1, 'day');
      weekIndex += 1;
    }

    monthHTML = `
      <h1>${firstDeliveryDate.format('MMMM YYYY')}</h1>
      <table data-firstDeliveryDate='${firstDeliveryDate.format('YYYY/MM/DD')}'>
        <thead>
          <tr>
            <td>MO</td>
            <td>TU</td>
            <td>WE</td>
            <td>TH</td>
            <td>FR</td>
            <td>SA</td>
            <td>SU</td>
          </tr>
        </thead>
        ${monthHTML}
      </table>
    `;

    return monthHTML;
  }


}

/**
 * this gets applied to the top left positioning pinning
 * @param element
 * @param target
 */

/**
 *
 * @param element
 * @param target
 * @returns {{top: *, left: number, width: *}}
 */
function offsetsAndDimensions(element, target) {

  const targetBoundingClientRect = target.getBoundingClientRect();

  const targetHeight = targetBoundingClientRect.height ? targetBoundingClientRect.height
    : (targetBoundingClientRect.bottom - targetBoundingClientRect.top);
  const targetWidth = targetBoundingClientRect.width ? targetBoundingClientRect.width
    : (targetBoundingClientRect.right - targetBoundingClientRect.left);

  return {
    top: targetHeight,
    left: 0,
    width: targetWidth
  };
}

// only compares date, the time is ignored
function isFirstDateSameOrBeforeSecondDate(firstDate, secondDate) {
  if (firstDate.year() === secondDate.year() &&
    firstDate.month() === secondDate.month() &&
    firstDate.date() === secondDate.date()) {
    return true;
  }

  if (firstDate.year() > secondDate.year()) {
    return false;
  }

  if (firstDate.year() < secondDate.year()) {
    return true;
  }

  if (firstDate.month() > secondDate.month()) {
    return false;
  }

  if (firstDate.month() < secondDate.month()) {
    return true;
  }

  if (firstDate.date() > secondDate.date()) {
    return false;
  }

  return true;
}
